import { useEffect, useState } from 'react';
import type { MembershipPlanView } from '@guanwei/shared';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { createOrder, fetchMembershipPlans, submitPayment } from '@/lib/business.api';
import { useAuthStore } from '@/lib/auth.store';
import { useToast } from '@/components/ui/Toast';

const CYCLE_LABEL: Record<MembershipPlanView['billingCycle'], string> = {
  monthly: '月',
  yearly: '年',
  lifetime: '永久',
};

export function MembershipPage() {
  const user = useAuthStore((s) => s.user);
  const showToast = useToast();
  const [plans, setPlans] = useState<MembershipPlanView[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingCode, setSubmittingCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchMembershipPlans()
      .then(setPlans)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (plan: MembershipPlanView) => {
    if (!user) {
      showToast('请先登录后购买会员');
      return;
    }
    setSubmittingCode(plan.code);
    setError(null);
    try {
      const order = await createOrder({
        productCode: `membership.${plan.code}`,
        channel: 'alipay',
      });
      if (!submitPayment(order)) showToast('订单已创建，但未获取到支付链接');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmittingCode(null);
    }
  };

  return (
    <main className="flex-1 px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-accent">Membership</p>
          <h1 className="mt-3 font-serif text-3xl text-fg sm:text-4xl">会员套餐</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-fg-2">
            解锁完整报告、关系合盘、流年、大运、追问与起卦额度。推荐选择观微会员作为主力套餐。
          </p>
        </div>

        {error && <Card className="p-4 text-sm text-danger">{error}</Card>}

        {loading ? (
          <Card className="p-6 text-center text-sm text-fg-muted sm:p-10">正在读取套餐…</Card>
        ) : plans.length === 0 ? (
          <Card className="p-6 text-center sm:p-10">
            <p className="font-serif text-xl text-fg">暂无会员套餐</p>
            <p className="mt-2 text-sm text-fg-muted">请先在管理员后台启用会员套餐。</p>
          </Card>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                variant={plan.recommended ? 'gold' : 'default'}
                className="relative flex flex-col p-5 sm:p-6"
              >
                {plan.recommended && (
                  <span className="absolute right-4 top-4 rounded-full bg-accent px-3 py-1 text-xs text-accent-fg">
                    推荐
                  </span>
                )}
                <p className="text-xs uppercase tracking-[0.2em] text-fg-muted">{plan.tier}</p>
                <h2 className="mt-3 font-serif text-2xl text-fg">{plan.name}</h2>
                <p className="mt-2 min-h-12 text-sm leading-6 text-fg-2">
                  {plan.description ?? '会员权益套餐'}
                </p>
                <div className="mt-5 flex flex-wrap items-end gap-2">
                  <span className="font-serif text-3xl text-accent sm:text-4xl">
                    ¥{plan.priceYuan}
                  </span>
                  <span className="mb-1 text-sm text-fg-muted">
                    / {CYCLE_LABEL[plan.billingCycle]}
                  </span>
                </div>
                {plan.originalPriceYuan && (
                  <p className="mt-1 text-xs text-fg-muted line-through">
                    原价 ¥{plan.originalPriceYuan}
                  </p>
                )}

                <ul className="mt-6 flex-1 space-y-3 text-sm text-fg-1">
                  {plan.benefits.map((benefit) => (
                    <li key={benefit.id} className="flex gap-2">
                      <span className="mt-1 text-accent">◆</span>
                      <span>{benefit.label}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="mt-6 w-full"
                  size="lg"
                  onClick={() => void buy(plan)}
                  disabled={submittingCode !== null}
                >
                  {submittingCode === plan.code ? '生成订单…' : '立即开通'}
                </Button>
              </Card>
            ))}
          </div>
        )}

        {!user && (
          <p className="text-center text-sm text-fg-muted">
            已有账号？
            <Link to="/auth/login" className="text-accent hover:text-accent-hover">
              先登录再开通
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
